/**
 * PERSONA 5 ROYAL MASTER NAVIGATION CONTROLLER
 * Coordinates Timeline Engine, Cursor Physics, Visual FX, and Shopify Integration
 */

class Persona5MasterNavigation {
  constructor() {
    this.overlay = document.querySelector('#p5rNavOverlay');
    if (!this.overlay) return;

    this.triggers = document.querySelectorAll('.p5r-menu-trigger, [data-p5r-open]');
    this.closeBtn = this.overlay.querySelector('.p5r-close-trigger');
    
    this.isOpen = false;
    this.motion = null;
    this.physics = null;
    this.effects = null;

    this.init();
  }

  init() {
    // Instantiate Sub-Modules
    if (typeof window.Persona5NavigationMotion !== 'undefined') {
      this.motion = new window.Persona5NavigationMotion(this.overlay);
    }
    
    if (typeof window.Persona5CursorPhysics !== 'undefined') {
      this.physics = new window.Persona5CursorPhysics(this.overlay);
    }

    if (typeof window.Persona5Effects !== 'undefined') {
      this.effects = new window.Persona5Effects(this.overlay);
    }

    this.bindEvents();
  }

  bindEvents() {
    // Open Triggers
    this.triggers.forEach(trigger => {
      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        this.open();
      });
    });

    // Close Button
    if (this.closeBtn) {
      this.closeBtn.addEventListener('click', (e) => {
        e.preventDefault();
        this.close();
      });
    }

    // Close on Empty Space / Backdrop Click
    this.overlay.addEventListener('click', (e) => {
      if (e.target.closest('a, button, input, textarea, .p5r-menu-card-inner, .p5r-menu-card')) {
        return;
      }
      this.close();
    });

    // Menu Item Links & Home Back Button Page Exit Transition
    const links = this.overlay.querySelectorAll('.p5r-menu-card-link, .p5r-nav-home-back-btn');
    links.forEach(link => {
      link.addEventListener('click', (e) => {
        const targetUrl = link.getAttribute('href');
        if (targetUrl && targetUrl !== '#' && this.effects) {
          e.preventDefault();
          this.effects.triggerPageExitWipe(targetUrl);
        }
      });
    });

    // Keyboard ESC listener to toggle/open menu
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        // Ignore if user is actively typing inside an editable form field
        const tag = document.activeElement ? document.activeElement.tagName : '';
        if (tag === 'INPUT' || tag === 'TEXTAREA' || document.activeElement.isContentEditable) {
          return;
        }

        // If Cart Overlay/Drawer is currently open, DO NOT open the Navigation Menu!
        const cartOverlay = document.getElementById('p5rCartOverlay');
        const isCartOpen = (window.P5RCart && window.P5RCart.isOpen) ||
                           (cartOverlay && cartOverlay.classList.contains('is-active')) ||
                           document.body.classList.contains('p5r-cart-open');

        if (isCartOpen && !this.isOpen) {
          return; // Allow Cart listener to close the cart drawer exclusively
        }

        e.preventDefault();
        this.toggle();
      }

      if (this.isOpen && e.key === 'Tab') {
        this.handleFocusTrap(e);
      }
    });
  }

  toggle() {
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  open() {
    if (this.isOpen) return;
    this.isOpen = true;

    this.overlay.classList.add('is-active');
    document.body.classList.add('p5r-menu-open');
    this.overlay.setAttribute('aria-hidden', 'false');
    this.updateTriggerAria(true);

    if (this.motion) {
      this.motion.play();
    }

    if (this.physics) {
      this.physics.start();
    }
  }

  close() {
    if (!this.isOpen) return;
    this.isOpen = false;

    if (this.physics) {
      this.physics.stop();
    }

    const finishClose = () => {
      this.overlay.classList.remove('is-active');
      document.body.classList.remove('p5r-menu-open');
      this.overlay.setAttribute('aria-hidden', 'true');
      this.updateTriggerAria(false);
    };

    if (this.motion && typeof this.motion.exit === 'function') {
      this.motion.exit(finishClose);
    } else {
      finishClose();
    }
  }

  handleFocusTrap(e) {
    const focusable = this.overlay.querySelectorAll('a[href], button, input, [tabindex]:not([tabindex="-1"])');
    if (focusable.length === 0) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  updateTriggerAria(expanded) {
    this.triggers.forEach(trigger => {
      trigger.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    });
  }
}

let p5rNavInstance = null;

function initP5RNavigation() {
  if (p5rNavInstance) {
    // Basic cleanup before reinit
    const oldTriggers = document.querySelectorAll('.p5r-hud-menu-btn, .p5r-mobile-nav-toggle');
    oldTriggers.forEach(t => {
      const newT = t.cloneNode(true);
      if(t.parentNode) t.parentNode.replaceChild(newT, t);
    });
  }
  p5rNavInstance = new Persona5MasterNavigation();
}

// Auto-initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initP5RNavigation);
} else {
  initP5RNavigation();
}

document.addEventListener('shopify:section:load', (e) => {
  if (e.detail.sectionId === 'navigation-overlay' || e.detail.sectionId === 'header') {
    initP5RNavigation();
  }
});
